import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import CloseIcon from "@mui/icons-material/Close";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ListEmpty from "../GeneralComponents/ListEmpty";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const SEVERIDADES = {
  error: {
    label: "Error",
    color: "#C62828",
    bg: "rgba(198, 40, 40, 0.08)",
    icon: <ErrorOutlineIcon fontSize="small" />,
  },
  advertencia: {
    label: "Advertencia",
    color: "#E65100",
    bg: "rgba(230, 81, 0, 0.08)",
    icon: <WarningAmberIcon fontSize="small" />,
  },
  info: {
    label: "Informativo",
    color: "#1565C0",
    bg: "rgba(21, 101, 192, 0.08)",
    icon: <InfoOutlinedIcon fontSize="small" />,
  },
};

const GROUP_BG = "rgba(25, 118, 210, 0.10)";
const FILAS_DETALLE = 200;

// Los conteos se piden en tandas para que ningún request se acerque al
// límite de tiempo de la capa HTTP: ejecutarlos todos juntos lo supera.
const CONTEOS_POR_TANDA = 4;
const TANDAS_EN_PARALELO = 3;

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

/** Periodos seleccionables: los últimos 24 meses, como 'YYYY-MM-01'. */
function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 24 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

/** '2026-06-01' -> 'Junio 2026' (sin depender del locale global de dayjs). */
function etiquetaPeriodo(periodo) {
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function describirError(e) {
  const status = e?.response?.status;
  if (status === 403) return "No tienes privilegios para acceder al monitoreo.";
  if (status === 404) return "El servicio de monitoreo no está disponible.";
  return "No se pudieron obtener los datos. Intenta nuevamente.";
}

function formatoMonto(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL", {
    style: "currency",
    currency: "CLP",
    maximumFractionDigits: 0,
  });
}

function TarjetaResumen({ titulo, valor }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 160,
        px: 2,
        py: 1.5,
        borderRight: "1px solid rgba(0,0,0,0.12)",
        "&:last-of-type": { borderRight: "none" },
      }}
    >
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)" }}>
        {titulo}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        {valor}
      </Typography>
    </Box>
  );
}

function FilaChequeo({ check, seleccionado, onSelect, onReintentar }) {
  const sev = SEVERIDADES[check.severidad] ?? SEVERIDADES.info;
  const conHallazgos = (check.total ?? 0) > 0;
  const fallo = check.estado === "error";
  const pendiente = check.estado === "pendiente";

  return (
    <Box
      onClick={() => conHallazgos && onSelect(check)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        cursor: conHallazgos ? "pointer" : "default",
        background: seleccionado ? GROUP_BG : "transparent",
        "&:hover": { background: conHallazgos ? "rgba(0,0,0,0.03)" : "transparent" },
      }}
    >
      <Box sx={{ color: conHallazgos ? sev.color : "rgba(0,0,0,0.26)", display: "flex" }}>
        {pendiente ? (
          <CircularProgress size={18} />
        ) : conHallazgos ? (
          sev.icon
        ) : (
          <CheckCircleOutlineIcon fontSize="small" />
        )}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: conHallazgos ? 600 : 400 }}>
          {check.nombre}
        </Typography>
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
          {check.descripcion}
        </Typography>
      </Box>

      {pendiente ? (
        <Chip label="…" size="small" variant="outlined" sx={{ minWidth: 64 }} />
      ) : fallo ? (
        <Tooltip title={`${check.error ?? "El chequeo no pudo ejecutarse"} Clic para reintentar.`}>
          <Chip
            label="Sin ejecutar"
            size="small"
            variant="outlined"
            deleteIcon={<ReplayIcon />}
            onDelete={(e) => {
              e.stopPropagation();
              onReintentar?.(check);
            }}
            onClick={(e) => {
              e.stopPropagation();
              onReintentar?.(check);
            }}
          />
        </Tooltip>
      ) : (
        <Chip
          label={(check.total ?? 0).toLocaleString("es-CL")}
          size="small"
          sx={{
            minWidth: 64,
            fontWeight: 700,
            color: conHallazgos ? sev.color : "rgba(0,0,0,0.5)",
            background: conHallazgos ? sev.bg : "rgba(0,0,0,0.05)",
          }}
        />
      )}

      {conHallazgos && (
        <Button size="small" sx={{ textTransform: "none" }}>
          Ver detalle
        </Button>
      )}
    </Box>
  );
}

export default function MonitoreoAdministracion() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [periodoPendiente, setPeriodoPendiente] = useState(periodos[1] ?? periodos[0]);
  const [operadorPendiente, setOperadorPendiente] = useState("");

  const [periodo, setPeriodo] = useState(periodos[1] ?? periodos[0]);
  const [idOperador, setIdOperador] = useState("");

  const [operadores, setOperadores] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [refetch, setRefetch] = useState(0);
  const [conteosPendientes, setConteosPendientes] = useState(0);

  const [soloHallazgos, setSoloHallazgos] = useState(true);

  const [checkSel, setCheckSel] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [detalleLoading, setDetalleLoading] = useState(false);
  const [detallePage, setDetallePage] = useState(0);

  useEffect(() => {
    setPageTitle?.("MONITOREO ADMINISTRACIÓN");
  }, []);

  useEffect(() => {
    axios
      .getAllOperadores()
      .then((res) => setOperadores(res?.data ?? []))
      .catch(() => setOperadores([]));
  }, []);

  // Cada carga toma un id; una ejecución en curso se descarta si el id cambió
  // (el usuario cambió de periodo antes de que terminaran las tandas).
  const ejecucionRef = useRef(0);

  const correrConteos = useCallback(
    async (codigos, { tamanoTanda = CONTEOS_POR_TANDA, ejecucion } = {}) => {
      if (!codigos.length) return;
      const id_operador = idOperador === "" ? null : Number(idOperador);
      const miEjecucion = ejecucion ?? ejecucionRef.current;
      const vigente = () => ejecucionRef.current === miEjecucion;

      const tandas = [];
      for (let i = 0; i < codigos.length; i += tamanoTanda) {
        tandas.push(codigos.slice(i, i + tamanoTanda));
      }

      setConteosPendientes((n) => n + tandas.length);
      setResumen((prev) =>
        prev
          ? {
              ...prev,
              checks: prev.checks.map((c) =>
                codigos.includes(c.codigo)
                  ? { ...c, estado: "pendiente", total: null, error: undefined }
                  : c
              ),
            }
          : prev
      );

      const aplicar = (conteos) => {
        if (!vigente()) return;
        setResumen((prev) => {
          if (!prev) return prev;
          const porCodigo = Object.fromEntries(conteos.map((c) => [c.codigo, c]));
          return {
            ...prev,
            checks: prev.checks.map((c) =>
              porCodigo[c.codigo] ? { ...c, ...porCodigo[c.codigo] } : c
            ),
          };
        });
        setConteosPendientes((n) => Math.max(0, n - 1));
      };

      let siguiente = 0;
      const worker = async () => {
        while (vigente()) {
          const idx = siguiente++;
          if (idx >= tandas.length) return;
          try {
            const res = await axios.getMonitoreoPrefacturaConteos({
              periodo,
              codigos: tandas[idx],
              id_operador,
            });
            aplicar(res?.data?.conteos ?? []);
          } catch {
            aplicar(
              tandas[idx].map((codigo) => ({
                codigo,
                total: null,
                estado: "error",
                error: "No se pudo completar el chequeo.",
              }))
            );
          }
        }
      };

      await Promise.all(
        Array.from({ length: Math.min(TANDAS_EN_PARALELO, tandas.length) }, worker)
      );
    },
    [periodo, idOperador]
  );

  useEffect(() => {
    const miEjecucion = ++ejecucionRef.current;

    const cargar = async () => {
      setLoading(true);
      setFetchError(null);
      setCheckSel(null);
      setDetalle(null);
      setConteosPendientes(0);

      const id_operador = idOperador === "" ? null : Number(idOperador);

      let base;
      try {
        const res = await axios.getMonitoreoPrefacturaResumen({ periodo, id_operador });
        base = res?.data ?? null;
      } catch (e) {
        if (ejecucionRef.current !== miEjecucion) return;
        setResumen(null);
        setFetchError(describirError(e));
        setLoading(false);
        return;
      }

      if (ejecucionRef.current !== miEjecucion || !base) return;
      setResumen(base);
      setLoading(false);

      await correrConteos(
        (base.checks ?? []).map((c) => c.codigo),
        { ejecucion: miEjecucion }
      );
    };

    cargar();
    return () => {
      ejecucionRef.current++;
    };
  }, [periodo, idOperador, refetch, correrConteos]);

  const codigosFallidos = useMemo(
    () => (resumen?.checks ?? []).filter((c) => c.estado === "error").map((c) => c.codigo),
    [resumen]
  );

  // Los que fallan suelen ser los más lentos: se reintentan de a uno por
  // request para darles el mayor tiempo posible antes de que expire.
  const reintentarFallidos = () => correrConteos(codigosFallidos, { tamanoTanda: 1 });

  const cargarDetalle = async (check, page = 0) => {
    setCheckSel(check);
    setDetalleLoading(true);
    setDetallePage(page);
    try {
      const res = await axios.getMonitoreoPrefacturaDetalle({
        codigo: check.codigo,
        periodo,
        id_operador: idOperador === "" ? null : Number(idOperador),
        limit: FILAS_DETALLE,
        offset: page * FILAS_DETALLE,
      });
      setDetalle(res?.data ?? null);
    } catch {
      setDetalle(null);
    } finally {
      setDetalleLoading(false);
    }
  };

  const cerrarDetalle = () => {
    setCheckSel(null);
    setDetalle(null);
    setDetallePage(0);
  };

  const handleAplicar = () => {
    setPeriodo(periodoPendiente);
    setIdOperador(operadorPendiente);
    setRefetch((t) => t + 1);
  };

  // El backend ya no envía los totales (los conteos llegan por tandas), así que
  // se recalculan acá a medida que se resuelven.
  const totales = useMemo(() => {
    const checks = resumen?.checks ?? [];
    const cuenta = (sev) =>
      checks.filter((c) => c.severidad === sev && (c.total ?? 0) > 0).length;
    return {
      error: cuenta("error"),
      advertencia: cuenta("advertencia"),
      info: cuenta("info"),
      sin_hallazgos: checks.filter((c) => c.estado === "ok" && (c.total ?? 0) === 0).length,
      chequeos_fallidos: checks.filter((c) => c.estado === "error").length,
    };
  }, [resumen]);

  const checksPorFamilia = useMemo(() => {
    if (!resumen) return [];
    const visibles = soloHallazgos
      ? resumen.checks.filter(
          (c) => (c.total ?? 0) > 0 || c.estado === "error" || c.estado === "pendiente"
        )
      : resumen.checks;

    return (resumen.familias ?? []).map((familia) => {
      const items = visibles.filter((c) => c.familia === familia);
      const hallazgos = items.reduce((acc, c) => acc + (c.total ?? 0), 0);
      const criticos = items.filter(
        (c) => c.severidad === "error" && (c.total ?? 0) > 0
      ).length;
      // Una familia es independiente del periodo cuando todos sus chequeos lo son.
      const independiente =
        items.length > 0 && items.every((c) => c.depende_periodo === false);
      return { familia, items, hallazgos, criticos, independiente };
    });
  }, [resumen, soloHallazgos]);

  const descargarCSV = () => {
    if (!detalle?.filas?.length) return;
    const cols = detalle.columnas ?? [];
    const headers = cols.map((c) => c.label);
    const rows = detalle.filas.map((f) =>
      cols
        .map((c) => `"${String(f[c.key] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `monitoreo_${detalle.codigo}_${periodo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const universo = resumen?.universo ?? {};

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          MONITOREO ADMINISTRACIÓN
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Chequeos de integridad sobre los RBD con{" "}
          <strong>Estado de pago: Facturar</strong> en el periodo seleccionado.
        </Typography>

        {/* Filtros */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 2,
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Periodo:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={periodoPendiente}
              onChange={(e) => setPeriodoPendiente(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 280px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operador:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={operadorPendiente}
              onChange={(e) => setOperadorPendiente(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {operadores.map((o) => (
                <MenuItem key={o.id_operador} value={o.id_operador}>
                  {o.nombre_operador}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Button
            variant="contained"
            onClick={handleAplicar}
            sx={{
              background: "#C8D8F0",
              color: "#1565c0",
              boxShadow: "none",
              "&:hover": { background: "#b3c8e8", boxShadow: "none" },
            }}
          >
            Aplicar
          </Button>

          <Box sx={{ flexGrow: 1 }} />

          <Button
            variant="outlined"
            onClick={() => setSoloHallazgos((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            {soloHallazgos ? "Mostrar todos los chequeos" : "Solo con hallazgos"}
          </Button>
        </Box>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "40vh",
            }}
          >
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>Ejecutando chequeos...</Typography>
          </Box>
        ) : fetchError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "40vh",
            }}
          >
            <Typography color="error" sx={{ mb: 2 }}>
              {fetchError}
            </Typography>
            <Button variant="contained" onClick={() => setRefetch((t) => t + 1)}>
              Reintentar
            </Button>
          </Box>
        ) : (
          <>
            {/* Resumen del universo */}
            <Paper elevation={0} sx={{ mb: 3, border: "1px solid rgba(0,0,0,0.15)" }}>
              <Box sx={{ display: "flex", flexWrap: "wrap" }}>
                <TarjetaResumen
                  titulo="RBD facturando"
                  valor={(universo.rbds_facturando ?? 0).toLocaleString("es-CL")}
                />
                <TarjetaResumen
                  titulo="Con cálculo"
                  valor={(universo.rbds_calculados ?? 0).toLocaleString("es-CL")}
                />
                <TarjetaResumen
                  titulo="Operadores"
                  valor={(universo.operadores ?? 0).toLocaleString("es-CL")}
                />
                <TarjetaResumen
                  titulo="Prefacturas emitidas"
                  valor={(universo.prefacturas_operador ?? 0).toLocaleString("es-CL")}
                />
                <TarjetaResumen titulo="Monto del periodo" valor={formatoMonto(universo.monto_total)} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderTop: "1px solid rgba(0,0,0,0.12)",
                  background: "#FAFAFA",
                }}
              >
                {["error", "advertencia", "info"].map((sev) => (
                  <Chip
                    key={sev}
                    icon={SEVERIDADES[sev].icon}
                    label={`${totales[sev] ?? 0} ${SEVERIDADES[sev].label.toLowerCase()}${
                      (totales[sev] ?? 0) === 1 ? "" : "s"
                    }`}
                    size="small"
                    sx={{
                      fontWeight: 600,
                      color: SEVERIDADES[sev].color,
                      background: SEVERIDADES[sev].bg,
                      "& .MuiChip-icon": { color: SEVERIDADES[sev].color },
                    }}
                  />
                ))}
                <Chip
                  icon={<CheckCircleOutlineIcon fontSize="small" />}
                  label={`${totales.sin_hallazgos ?? 0} sin hallazgos`}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    color: "#2E7D32",
                    background: "rgba(46, 125, 50, 0.08)",
                    "& .MuiChip-icon": { color: "#2E7D32" },
                  }}
                />
                {(totales.chequeos_fallidos ?? 0) > 0 && (
                  <Chip
                    label={`${totales.chequeos_fallidos} sin ejecutar`}
                    size="small"
                    variant="outlined"
                    onClick={reintentarFallidos}
                    onDelete={reintentarFallidos}
                    deleteIcon={<ReplayIcon />}
                    disabled={conteosPendientes > 0}
                    title="Reintentar los chequeos que no se pudieron ejecutar"
                  />
                )}
                {conteosPendientes > 0 && (
                  <Chip
                    icon={<CircularProgress size={14} />}
                    label="Ejecutando chequeos..."
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Paper>

            {/* Chequeos por familia */}
            <PageBgBlock pad="0">
              {checksPorFamilia.every((g) => g.items.length === 0) ? (
                <Paper elevation={0} sx={{ width: "100%", p: 3 }}>
                  <Typography variant="body2" sx={{ textAlign: "center" }}>
                    Sin hallazgos para el periodo seleccionado.
                  </Typography>
                </Paper>
              ) : (
                checksPorFamilia
                  .filter((g) => g.items.length > 0)
                  .map((g) => (
                    <Accordion
                      key={g.familia}
                      variant="outlined"
                      sx={{ mb: 2 }}
                      defaultExpanded
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          background: GROUP_BG,
                          borderBottom: "1px solid rgba(0,0,0,0.12)",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {g.familia}&nbsp;&nbsp;
                            <Box
                              component="span"
                              sx={{ fontWeight: 400, color: "rgba(0,0,0,0.60)" }}
                            >
                              {g.hallazgos.toLocaleString("es-CL")} hallazgo
                              {g.hallazgos === 1 ? "" : "s"} en {g.items.length} chequeo
                              {g.items.length === 1 ? "" : "s"}
                            </Box>
                          </Typography>
                          {g.independiente && (
                            <Tooltip title="Estos chequeos no dependen del periodo seleccionado: muestran el estado actual de los datos.">
                              <Chip
                                icon={<AllInclusiveIcon fontSize="small" />}
                                label="Independiente del periodo"
                                size="small"
                                variant="outlined"
                                sx={{ background: "#fff" }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {g.items.map((c) => (
                          <FilaChequeo
                            key={c.codigo}
                            check={c}
                            seleccionado={checkSel?.codigo === c.codigo}
                            onSelect={(chk) => cargarDetalle(chk, 0)}
                            onReintentar={(chk) =>
                              correrConteos([chk.codigo], { tamanoTanda: 1 })
                            }
                          />
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  ))
              )}
            </PageBgBlock>

          </>
        )}
      </Box>

      <Dialog
        open={Boolean(checkSel)}
        onClose={cerrarDetalle}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            {checkSel && (
              <Box
                sx={{
                  color: (SEVERIDADES[checkSel.severidad] ?? SEVERIDADES.info).color,
                  display: "flex",
                  mt: 0.5,
                }}
              >
                {(SEVERIDADES[checkSel.severidad] ?? SEVERIDADES.info).icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {checkSel?.nombre}
              </Typography>
              <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)", mt: 0.5 }}>
                {checkSel?.descripcion}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
                {checkSel?.impacto}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={cerrarDetalle}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {detalleLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {(detalle?.columnas ?? []).map((c) => (
                      <TableCell key={c.key} sx={{ fontWeight: 600 }}>
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!detalle?.filas?.length ? (
                    <TableRow>
                      <TableCell colSpan={(detalle?.columnas ?? []).length || 1}>
                        <ListEmpty />
                      </TableCell>
                    </TableRow>
                  ) : (
                    detalle.filas.map((f, idx) => (
                      <TableRow key={idx} hover>
                        {detalle.columnas.map((c) => (
                          <TableCell key={c.key}>
                            {f[c.key] == null || f[c.key] === "" ? "—" : String(f[c.key])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "space-between", px: 2 }}>
          <TablePagination
            component="div"
            count={detalle?.total ?? 0}
            page={detallePage}
            onPageChange={(_, p) => cargarDetalle(checkSel, p)}
            rowsPerPage={FILAS_DETALLE}
            rowsPerPageOptions={[FILAS_DETALLE]}
            labelRowsPerPage="Filas por página:"
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={descargarCSV}
              disabled={!detalle?.filas?.length}
              sx={{ textTransform: "none" }}
            >
              Descargar (.csv)
            </Button>
            <Button size="small" onClick={cerrarDetalle} sx={{ textTransform: "none" }}>
              Cerrar
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </PageBody>
  );
}
